import { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { MutagentError, ApiError, handleError } from '../../lib/errors.js';
import {
  promptDatasetsLink, datasetLinks, datasetLink,
  formatCreationHints,
  datasetAddedDirective, datasetDeletedDirective,
  echoDirectiveToStderr,
} from '../../lib/ui-links.js';
import type { ResourceState } from '../../lib/ui-links.js';
import { updateMutationContext, type DeleteOptions } from './index.js';
import { fetchAndBuildGuidedDatasetWorkflow, buildGenericDatasetWorkflow } from './guided-dataset.js';

interface DatasetAddOptions {
  data?: string;
  name?: string;
  guided?: boolean;
}

/**
 * Parse dataset file content based on format detection.
 * Supports JSON arrays, JSONL (one JSON object per line), and CSV passthrough.
 *
 * @param rawContent - The raw file content as a string
 * @param filePath - The file path (used for format detection and error messages)
 * @returns The parsed content as a JSON string suitable for the API
 */
export function parseDatasetFile(rawContent: string, filePath: string): string {
  const trimmed = rawContent.trim();

  // CSV files are passed through as-is (the API handles CSV parsing)
  if (filePath.endsWith('.csv')) {
    return rawContent;
  }

  // If content starts with '[', treat as JSON array
  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (!Array.isArray(parsed)) {
        throw new MutagentError(
          'INVALID_JSON',
          'Expected a JSON array in dataset file',
          'Run: mutagent prompts dataset add --help\nDataset JSON files should contain an array of objects: [{...}, {...}]'
        );
      }
      return trimmed;
    } catch (e) {
      if (e instanceof MutagentError) throw e;
      throw new MutagentError(
        'INVALID_JSON',
        `Failed to parse JSON array from ${filePath}`,
        'Run: mutagent prompts dataset add --help\nEnsure the file contains valid JSON. For line-delimited JSON, use .jsonl extension or put one object per line.'
      );
    }
  }

  // If content starts with '{' or doesn't start with '[', treat as JSONL
  // (one JSON object per line)
  const lines = trimmed.split('\n').filter(line => line.trim().length > 0);
  const items: unknown[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    const trimmedLine = line.trim();
    try {
      items.push(JSON.parse(trimmedLine));
    } catch {
      throw new MutagentError(
        'INVALID_JSONL',
        `Invalid JSON on line ${String(i + 1)} of ${filePath}`,
        `Run: mutagent prompts dataset add --help\nEach line must be valid JSON. Problem line: "${trimmedLine.substring(0, 80)}${trimmedLine.length > 80 ? '...' : ''}"`
      );
    }
  }

  return JSON.stringify(items);
}

export function registerDatasetCommands(prompts: Command): void {
  const dataset = new Command('dataset')
    .description('Manage datasets for prompts')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts dataset list <prompt-id>
  ${chalk.dim('$')} mutagent prompts dataset get <dataset-id>
  ${chalk.dim('$')} mutagent prompts dataset add <prompt-id> -d '[{"input":{...},"expectedOutput":{...}}]'
  ${chalk.dim('$')} mutagent prompts dataset delete <prompt-id> <dataset-id>
  ${chalk.dim('$')} mutagent prompts dataset items get <item-id>
    `)
    .action(() => {
      dataset.help();
    });
  prompts.addCommand(dataset);

  // List datasets
  dataset
    .command('list')
    .description('List datasets for a prompt')
    .argument('<prompt-id>', 'Prompt ID (from: mutagent prompts list)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts dataset list <prompt-id>
  ${chalk.dim('$')} mutagent prompts dataset list <prompt-id> --json
    `)
    .action(async (promptId: string) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const datasets = await client.listDatasets(promptId);

        if (isJson) {
          // Summary view — only IDs and key fields. Use `prompts dataset get` for full details.
          const withLinks = datasets.map((d) => ({
            id: d.id,
            name: d.name,
            itemCount: d.itemCount,
            updatedAt: d.updatedAt,
            _links: datasetLinks(promptId, d.id),
          }));
          output.output(withLinks);
        } else {
          const formatted = datasets.map((d) => ({
            id: d.id,
            name: d.name,
            itemCount: d.itemCount,
            updated: d.updatedAt ? new Date(String(d.updatedAt)).toLocaleDateString() : 'N/A',
            url: datasetLink(promptId, d.id),
          }));
          output.output(formatted);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Get dataset (single dataset details)
  dataset
    .command('get')
    .description('Get full details of a single dataset')
    .argument('<dataset-id>', 'Dataset ID (from: mutagent prompts dataset list <prompt-id>)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts dataset get <dataset-id>
  ${chalk.dim('$')} mutagent prompts dataset get <dataset-id> --json

${chalk.dim('Tip: Use this after `dataset list` to drill into a single dataset.')}
    `)
    .action(async (datasetId: string) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        if (!/^\d+$/.test(datasetId)) {
          throw new MutagentError(
            'INVALID_ARGUMENT',
            `Invalid dataset ID: "${datasetId}" (must be numeric)`,
            'Run: mutagent prompts dataset list <prompt-id>\nDataset IDs are numeric. Example: mutagent prompts dataset get 42'
          );
        }

        const client = await getSDKClient();
        const ds = await client.getDataset(datasetId);
        const promptGroupId = ds.promptGroupId;

        if (isJson) {
          output.output({
            ...ds,
            _links: datasetLinks(promptGroupId, ds.id),
          });
        } else {
          output.success(`Dataset: ${ds.name} (id: ${String(ds.id)})`);
          if (ds.description) output.info(`Description: ${ds.description}`);
          if (ds.itemCount !== undefined) output.info(`Items: ${String(ds.itemCount)}`);
          if (ds.updatedAt) output.info(`Updated: ${new Date(String(ds.updatedAt)).toLocaleString()}`);
          if (ds.createdAt) output.info(`Created: ${new Date(String(ds.createdAt)).toLocaleString()}`);
          if (ds.createdBy) output.info(`Created by: ${ds.createdBy}`);
          if (ds.labels && ds.labels.length > 0) output.info(`Labels: ${ds.labels.join(', ')}`);
          const hints = formatCreationHints({
            resourceType: 'Dataset',
            id: ds.id,
            name: ds.name,
            dashboardUrl: datasetLink(promptGroupId, ds.id),
            apiPath: `/api/prompts/datasets/${String(ds.id)}`,
          });
          console.log(hints);
        }
      } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
          handleError(
            new MutagentError(
              'NOT_FOUND',
              `Dataset ${datasetId} not found`,
              `Run: mutagent prompts dataset list <prompt-id>\nVerify the dataset ID exists, or list datasets for a prompt to find valid IDs.`
            ),
            isJson,
          );
        } else {
          handleError(error, isJson);
        }
      }
    });

  // Add dataset
  dataset
    .command('add')
    .description('Add dataset to a prompt')
    .argument('[prompt-id]', 'Prompt ID (from: mutagent prompts list) — optional when --guided is set')
    .option('-d, --data <json>', 'Inline JSON array of dataset items')
    .option('-n, --name <name>', 'Dataset name')
    .option('--guided', 'Guided mode — analyze prompt schema and suggest dataset categories. prompt-id is optional when --guided is set.')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts dataset add --guided --json                 # no prompt-id: returns generic workflow + upload instructions
  ${chalk.dim('$')} mutagent prompts dataset add <prompt-id> --guided --json    # recommended: schema-aware category suggestions
  ${chalk.dim('$')} mutagent prompts dataset add <prompt-id> -d '[{"input":{"text":"hello"},"expectedOutput":{"result":"world"}}]'
  ${chalk.dim('$')} mutagent prompts dataset add <prompt-id> -d '[{"input":{"text":"hello"},"expectedOutput":{"result":"world"}}]' --name "My Dataset"

Guided mode (--guided):
  Fetches the prompt's inputSchema + outputSchema and returns structured JSON
  that guides a coding agent to collect domain-specific dataset items.
  The agent uses the guidance to ask the user questions, then constructs
  the dataset JSON and calls: mutagent prompts dataset add <id> -d '<json>' --name '<name>' --json

Inline data format (-d):
  JSON array of objects, e.g.:
  ${chalk.dim('[{"input": {"text": "hello"}, "expectedOutput": {"result": "world"}}]')}

Expected item format:
  ${chalk.dim('{"input": {"<field>": "<value>"}, "expectedOutput": {"<field>": "<value>"}}')}

${chalk.yellow('AI Agent (MANDATORY):')}
  ALWAYS use --json: mutagent prompts dataset add <id> -d '[...]' --json
  Items MUST have BOTH input AND expectedOutput.
  Keys must match prompt's inputSchema.properties (input) and outputSchema.properties (expectedOutput).
  expectedOutput is REQUIRED for evaluation scoring.
  Check schemas: mutagent prompts get <prompt-id> --json

${chalk.red('Required: --data or --guided must be provided.')}
    `)
    .action(async (promptId: string | undefined, options: DatasetAddOptions) => {
      // --guided implies --json
      const isJson = options.guided ? true : getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // --guided without prompt-id: return generic workflow template
        if (options.guided && !promptId) {
          const result = buildGenericDatasetWorkflow();
          output.output(result);
          return;
        }

        // Guided mode with prompt-id: fetch prompt schema and return structured guidance
        if (options.guided) {
          const guidedResult = await fetchAndBuildGuidedDatasetWorkflow(promptId!);
          output.output(guidedResult);
          return;
        }

        // Non-guided without prompt-id: error
        if (!promptId) {
          throw new MutagentError(
            'MISSING_ARGUMENTS',
            'prompt-id is required',
            'Run: mutagent prompts dataset add --help\n' +
            'Usage: mutagent prompts dataset add <prompt-id> [options]\n' +
            'With --guided: mutagent prompts dataset add --guided --json (returns generic workflow)'
          );
        }

        if (!options.data) {
          throw new MutagentError(
            'MISSING_ARGUMENTS',
            '-d/--data is required',
            'Run: mutagent prompts dataset add --help\n' +
            'Use -d \'[{...}]\' to provide inline JSON data'
          );
        }

        let content: string;

        // Inline JSON data - validate it parses as an array
        try {
          const parsed: unknown = JSON.parse(options.data);
          if (!Array.isArray(parsed)) {
            throw new MutagentError(
              'INVALID_JSON',
              'Inline data must be a JSON array',
              'Run: mutagent prompts dataset add --help\n' +
              'Provide a JSON array, e.g., \'[{"input": {...}, "expectedOutput": {...}}]\''
            );
          }
          content = options.data;
        } catch (e) {
          if (e instanceof MutagentError) throw e;
          throw new MutagentError(
            'INVALID_JSON',
            'Invalid JSON in -d/--data flag',
            'Run: mutagent prompts dataset add --help\n' +
            'Provide a valid JSON array, e.g., \'[{"input": {...}, "expectedOutput": {...}}]\''
          );
        }

        // Validate items in the array
        const parsedItems = JSON.parse(content) as unknown[];

        // Empty array check
        if (parsedItems.length === 0) {
          throw new MutagentError(
            'VALIDATION_ERROR',
            'Dataset cannot be empty',
            'Run: mutagent prompts dataset add --help\nProvide at least one item in the array.'
          );
        }

        // Per-item validation
        const warnings: string[] = [];
        for (let i = 0; i < parsedItems.length; i++) {
          const item = parsedItems[i] as Record<string, unknown>;
          // Check input exists
          if (!('input' in item)) {
            throw new MutagentError(
              'VALIDATION_ERROR',
              `Dataset item at index ${i} is missing required field "input"`,
              'Run: mutagent prompts dataset add --help\nEach item must have: {"input": {...}, "expectedOutput": {...}}'
            );
          }
          // Check input is an object
          if (typeof item.input !== 'object' || item.input === null || Array.isArray(item.input)) {
            throw new MutagentError(
              'VALIDATION_ERROR',
              `Dataset item at index ${i}: "input" must be an object, got ${Array.isArray(item.input) ? 'array' : typeof item.input}`,
              'Run: mutagent prompts dataset add --help\n"input" must be a JSON object: {"input": {"field": "value"}}'
            );
          }
          // Warning for missing expectedOutput
          if (!('expectedOutput' in item)) {
            warnings.push(`Item at index ${i} is missing "expectedOutput" (recommended for evaluation scoring)`);
          }
        }

        // Dataset naming: use --name flag or error
        const datasetName = options.name;
        if (!datasetName) {
          throw new MutagentError(
            'MISSING_ARGUMENTS',
            'Dataset name is required',
            'Run: mutagent prompts dataset add --help\n' +
            `[Agent: Ask the user for a dataset name via AskUserQuestion, then pass --name]\n` +
            `Use --name <name>, e.g., mutagent prompts dataset add ${promptId ?? '<prompt-id>'} --name "my-dataset" -d '[...]'`
          );
        }

        // promptId is guaranteed non-undefined here (guard above throws if missing)
        const resolvedPromptId = promptId!;
        const client = await getSDKClient();
        const datasetResult = await client.addDataset(resolvedPromptId, content, datasetName);

        if (isJson) {
          let rsState: ResourceState | undefined;
          try {
            const evals = await client.listEvaluations(resolvedPromptId);
            rsState = { evaluations: evals.length };
          } catch { /* non-critical */ }
          const directive = datasetAddedDirective(resolvedPromptId, datasetResult.id, datasetResult.name, datasetResult.itemCount, rsState);
          output.output({
            ...datasetResult,
            _links: datasetLinks(resolvedPromptId, datasetResult.id),
            _directive: directive,
          });
          echoDirectiveToStderr(directive);
        } else {
          // Show pre-upload warnings in non-json mode
          for (const w of warnings) {
            output.warn(w);
          }
          output.success(`Added dataset "${datasetResult.name}" to prompt: ${resolvedPromptId} (id: ${String(datasetResult.id)})`);
          if (datasetResult.itemCount !== undefined && datasetResult.itemCount > 0) {
            output.info(`Items uploaded: ${String(datasetResult.itemCount)}`);
          }
          if (datasetResult.missingExpectedOutputCount !== undefined && datasetResult.missingExpectedOutputCount > 0) {
            output.warn(
              `${String(datasetResult.missingExpectedOutputCount)} of ${String(datasetResult.itemCount ?? 0)} items have no expectedOutput. Optimization may fail without expected outputs.`
            );
          }
          const hints = formatCreationHints({
            resourceType: 'Dataset',
            id: datasetResult.id,
            name: datasetResult.name,
            dashboardUrl: datasetLink(resolvedPromptId, datasetResult.id),
            apiPath: `/api/prompts/${resolvedPromptId}/datasets/${String(datasetResult.id)}`,
          });
          console.log(hints);
        }

        // Auto-update mutation context
        updateMutationContext(ctx => {
          ctx.addDiscoveredDataset('inline-data', datasetResult.name, datasetResult.itemCount ?? 0);
          ctx.markDatasetUploaded('inline-data', String(datasetResult.id), resolvedPromptId);
        });
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Delete dataset
  dataset
    .command('delete')
    .description('Delete a dataset from a prompt')
    .argument('<prompt-id>', 'Prompt ID (from: mutagent prompts list)')
    .argument('<dataset-id>', 'Dataset ID (from: mutagent prompts dataset list <prompt-id>)')
    .option('--force', 'Skip confirmation')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts dataset delete <prompt-id> <dataset-id>
  ${chalk.dim('$')} mutagent prompts dataset delete <prompt-id> <dataset-id> --force
  ${chalk.dim('$')} mutagent prompts dataset delete <prompt-id> <dataset-id> --force --json
    `)
    .action(async (promptId: string, datasetId: string, options: DeleteOptions) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // In --json mode, auto-skip confirmation. Otherwise require --force.
        if (!options.force && !isJson) {
          throw new MutagentError(
            'CONFIRMATION_REQUIRED',
            `Deleting dataset ${datasetId} from prompt ${promptId} requires confirmation`,
            'Run: mutagent prompts dataset delete --help\n' +
            `[Agent: Ask the user to confirm via MultiChoice, then pass --force]\n` +
            `Use --force to confirm: mutagent prompts dataset delete ${promptId} ${datasetId} --force`
          );
        }

        const client = await getSDKClient();
        try {
          await client.deleteDataset(promptId, datasetId, { force: true });
        } catch (deleteError) {
          // 404 = already deleted = idempotent success
          if (deleteError instanceof ApiError && deleteError.statusCode === 404) {
            if (isJson) {
              const directive = datasetDeletedDirective(promptId, datasetId);
              output.output({ success: true, deletedId: datasetId, _links: { datasets: promptDatasetsLink(promptId) }, _directive: directive });
              echoDirectiveToStderr(directive);
            } else {
              output.success(`Dataset ${datasetId} already deleted (idempotent)`);
            }
            return;
          }
          throw deleteError;
        }

        if (isJson) {
          const directive = datasetDeletedDirective(promptId, datasetId);
          output.output({ success: true, deletedId: datasetId, _links: { datasets: promptDatasetsLink(promptId) }, _directive: directive });
          echoDirectiveToStderr(directive);
        } else {
          output.success(`Deleted dataset ${datasetId} from prompt ${promptId}`);
          output.info(`View datasets: ${promptDatasetsLink(promptId)}`);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // =========================================================================
  // Dataset Items subgroup
  // =========================================================================
  const items = new Command('items')
    .description('Manage individual dataset items')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts dataset items get <item-id>
  ${chalk.dim('$')} mutagent prompts dataset items get <item-id> --json
    `)
    .action(() => {
      items.help();
    });
  dataset.addCommand(items);

  // Get a single dataset item
  items
    .command('get')
    .description('Get full details of a single dataset item')
    .argument('<item-id>', 'Dataset item ID')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts dataset items get <item-id>
  ${chalk.dim('$')} mutagent prompts dataset items get <item-id> --json

${chalk.dim('Tip: Dataset item IDs are shown in dataset exports and optimization results.')}
    `)
    .action(async (itemId: string) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        if (!/^\d+$/.test(itemId)) {
          throw new MutagentError(
            'INVALID_ARGUMENT',
            `Invalid dataset item ID: "${itemId}" (must be numeric)`,
            'Run: mutagent prompts dataset items get --help\nDataset item IDs are numeric. Example: mutagent prompts dataset items get 17'
          );
        }

        const client = await getSDKClient();
        const item = await client.getDatasetItem(itemId);
        const itemWithDs = item as typeof item & { datasetId?: number | string };
        const dsId = itemWithDs.datasetId;

        if (isJson) {
          output.output({
            ...item,
            _links: {
              api: `/api/prompts/dataset-items/${String(item.id)}`,
              ...(dsId !== undefined ? { dataset: `/api/prompts/datasets/${String(dsId)}` } : {}),
            },
          });
        } else {
          output.success(`Dataset Item: ${String(item.id)}`);
          if (dsId !== undefined) output.info(`Dataset ID: ${String(dsId)}`);
          output.info(`Input: ${JSON.stringify(item.input, null, 2)}`);
          if (item.expectedOutput !== undefined) {
            output.info(`Expected output: ${typeof item.expectedOutput === 'string' ? item.expectedOutput : JSON.stringify(item.expectedOutput, null, 2)}`);
          }
          if (item.metadata && Object.keys(item.metadata).length > 0) {
            output.info(`Metadata: ${JSON.stringify(item.metadata)}`);
          }
          if (item.createdAt) output.info(`Created: ${new Date(String(item.createdAt)).toLocaleString()}`);
          const itemWithUpdated = item as typeof item & { updatedAt?: string };
          if (itemWithUpdated.updatedAt) output.info(`Updated: ${new Date(String(itemWithUpdated.updatedAt)).toLocaleString()}`);
        }
      } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
          handleError(
            new MutagentError(
              'NOT_FOUND',
              `Dataset item ${itemId} not found`,
              `Run: mutagent prompts dataset items get --help\nVerify the item ID exists. Use the dashboard or dataset exports to find valid item IDs.`
            ),
            isJson,
          );
        } else {
          handleError(error, isJson);
        }
      }
    });
}
